/**
 * ChecklistItem — A single checklist row with icon, editable title, and drag handle.
 *
 * V1-style "stay in place" keyboard behaviour — focus NEVER leaves the
 * current contentEditable during Enter/Backspace operations:
 *
 * - **Enter** → splits text at cursor. A new item with `textBefore` is
 *   created ABOVE. Current item keeps `textAfter` and retains focus at
 *   position 0. No focus() call needed.
 * - **Backspace at pos 0** → previous item's text is prepended into THIS
 *   item. Previous item is deleted. Focus stays here, cursor at the join
 *   point. No focus() call needed.
 * - **Backspace on empty** → deletes this row. Only case needing focus()
 *   to move to the previous item.
 * - **Escape** → reverts edits + blur.
 *
 * **Multi-user optimized:** Per-item `useLiveQueryImmediate` + React.memo
 * with ID comparison so sibling edits never cascade.
 */

import {
  memo,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  type FormEvent,
} from "react"
import { useTranslation } from "react-i18next"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { cn } from "@/shared/utils/general/cn"
import { ObjectStatus } from "@/db/schema"
import type { DbObject } from "@/db/schema"
import { updateChecklistTitle, toggleChecklistItem, deleteFile } from "@/db/operations"
import {
  useChecklistItemData,
  useChecklistFiles,
} from "@/features/task-detail/hooks/useChecklistItems"
import {
  getCursorOffset,
  setCursorOffset,
  getCursorRect,
  isCursorOnFirstLine,
  isCursorOnLastLine,
  hasNonTextChildren,
  stripLineBreaks,
} from "./cursorUtils"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import GripVertical from "@/shared/icons/pikaicons/stroke/general/draggable-six-dots.svg?react"
import ImagePlusIcon from "@/shared/icons/pikaicons/stroke/media/photo-image-plus.svg?react"
import CrossIcon from "@/shared/icons/totodoicons/stroke/cross.svg?react"
import { scrollChecklistRowIntoView } from "./scrollUtils"
import {
  getUserStatusDisplay,
  normalizeUserStatus,
} from "@/shared/constants/userStatus"
import { parseClipboardLines, type ParsedLine } from "./parseClipboardLines"
import { uploadChecklistImage, MAX_FILE_SIZE } from "@/shared/utils/tiptap-utils"
import { SwipeableChecklistRow, type ChecklistSwipeAction } from "./SwipeableChecklistRow"
import { ChecklistStatusMenu } from "./ChecklistStatusMenu"

// ---------------------------------------------------------------------------
// Sub-components: Image attachment
// ---------------------------------------------------------------------------

/** Button to add an image to a checklist item. Hidden file input + icon button. */
function ChecklistImageButton({
  itemId,
  disabled,
  isEditing,
}: {
  itemId: string
  disabled?: boolean
  isEditing: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const handleClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = ""
      inputRef.current.click()
    }
  }, [])

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file || !itemId) return
      if (file.size > MAX_FILE_SIZE) {
        console.error(`File too large: ${file.size} > ${MAX_FILE_SIZE}`)
        return
      }
      setUploading(true)
      try {
        await uploadChecklistImage(file, itemId)
      } catch (err) {
        console.error("[ChecklistImageButton] Upload failed:", err)
      } finally {
        setUploading(false)
      }
    },
    [itemId],
  )

  if (disabled) return null

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
      />
      <button
        type="button"
        onClick={handleClick}
        disabled={uploading}
        className={cn(
          "mt-1 shrink-0 p-0.5 rounded transition-opacity duration-150",
          // Permanently visible on touch (any-pointer: coarse covers touch
          // laptops too, where the primary pointer is a fine trackpad but
          // touch is also available); hover-only on mouse/trackpad devices.
          "opacity-0 any-pointer-coarse:opacity-40 group-hover:opacity-40 hover:!opacity-70",
          isEditing && "opacity-40",
          uploading && "opacity-100 animate-pulse",
        )}
        aria-label="Add image"
        tabIndex={-1}
      >
        <ImagePlusIcon width={14} height={14} style={{ color: "var(--muted-foreground)" }} />
      </button>
    </>
  )
}

/** Displays image thumbnails attached to a checklist item. */
function ChecklistImages({ itemId }: { itemId: string }) {
  const [files] = useChecklistFiles(itemId)

  const thumbnailUrls = useMemo(() => {
    return files
      .filter((f) => f.thumbnail)
      .map((f) => ({
        id: f.id!,
        url: URL.createObjectURL(
          new Blob([new Uint8Array(f.thumbnail!)], { type: "image/jpeg" }),
        ),
      }))
  }, [files])

  // Revoke old object URLs on change
  useEffect(() => {
    return () => {
      thumbnailUrls.forEach((t) => URL.revokeObjectURL(t.url))
    }
  }, [thumbnailUrls])

  const handleRemove = useCallback(async (fileId: string) => {
    try {
      await deleteFile(fileId)
    } catch (err) {
      console.error("[ChecklistImages] Delete failed:", err)
    }
  }, [])

  if (thumbnailUrls.length === 0) return null

  return (
    <div className="flex flex-wrap gap-1.5 pl-7 pt-1">
      {thumbnailUrls.map((t) => (
        <div key={t.id} className="group/img relative">
          <img
            src={t.url}
            alt=""
            className="h-12 w-12 rounded object-cover"
          />
          <button
            type="button"
            onClick={() => handleRemove(t.id)}
            className="absolute -top-1 -right-1 hidden size-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground group-hover/img:flex"
            aria-label="Remove image"
          >
            <CrossIcon width={10} height={10} />
          </button>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AUTO_SAVE_DELAY = 3_000


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChecklistItemProps {
  /** The checklist item's DB id — used as the sole memo key */
  id: string
  /** Whether this is rendered inside DragOverlay */
  isDragOverlay?: boolean
  /**
   * True while ANY row in this checklist is being dragged.
   *
   * Used to drop contentEditable for the duration of the drag. On iOS the
   * rows kept their chrome (checkbox, image icon, drag handle) but their TEXT
   * vanished mid-drag — only the contentEditable layer failed to repaint while
   * dnd-kit continuously updated the ancestor transform. The row stays fully
   * visible; it is only temporarily not editable, which costs nothing because
   * you cannot type while dragging.
   */
  isDragActive?: boolean
  /**
   * Optimistic item data from the container's merged items array.
   * Used as fallback until useChecklistItemData resolves from DB,
   * preventing the brief empty flash when a new item is created.
   */
  optimisticItem?: DbObject
  /**
   * Enter → create new item ABOVE with textBefore.
   * Current item keeps textAfter and retains focus (no focus move).
   */
  onSplitAtCursor?: (
    itemId: string,
    itemIndex: number,
    textBefore: string,
    textAfter: string,
  ) => void
  /**
   * Backspace at pos 0 (non-empty) → merge previous item's text into THIS item.
   * Previous item will be deleted. This item keeps focus.
   * Returns the previous item's title (for DOM prepend + cursor positioning)
   * or undefined if this is the first item (no merge).
   */
  onMergeWithPrevious?: (id: string, text: string) => string | undefined
  /**
   * Delete at end (non-empty) → merge next item's text into THIS item.
   * Next item will be deleted. This item keeps focus.
   * Returns the next item's title (for DOM append), or undefined if last item.
   */
  onMergeWithNext?: (id: string, text: string) => string | undefined
  /** Backspace on completely empty → delete + focus previous */
  onDeleteAndFocusPrevious?: (id: string) => void
  /** Context menu for status/progress */
  onOpenStatusMenu?: (id: string, anchorEl: HTMLElement) => void
  /** ArrowUp/Left at top/start → navigate to previous item (passes cursor X) */
  onNavigatePrevious?: (id: string, cursorX: number) => void
  /** ArrowDown/Right at bottom/end → navigate to next item (passes cursor X) */
  onNavigateNext?: (id: string, cursorX: number) => void
  /** Whether this item is in the multi-selection set */
  isSelected?: boolean
  /** Whether this item is the first in a contiguous selection group */
  isFirstSelected?: boolean
  /** Whether this item is the last in a contiguous selection group */
  isLastSelected?: boolean
  /** Whether this item is being dragged as part of a multi-selection (not the primary) */
  isDraggedAway?: boolean
  /** Notify container when this item receives contentEditable focus */
  onFocusItem?: (id: string) => void
  /** Notify container of a modifier-click (Ctrl/Shift) for multi-selection */
  onSelectItem?: (
    id: string,
    modifiers: { meta: boolean; shift: boolean },
  ) => void
  /** ⌘K → toggle complete/uncomplete */
  onToggleComplete?: (id: string) => void
  /** ⌥⌘K → toggle cancel/uncomplete */
  onToggleCancel?: (id: string) => void
  /** ⌘D → duplicate item below */
  onDuplicateItem?: (id: string) => void
  /** ⌘↑ → reorder item up */
  onReorderUp?: (id: string) => void
  /** ⌘↓ → reorder item down */
  onReorderDown?: (id: string) => void
  /** ⌘⇧H → convert to section (title passed when converting via # prefix) */
  onConvertToSection?: (id: string, title?: string) => void
  /** Shift+Arrow → extend checklist selection */
  onShiftArrow?: (id: string, direction: "up" | "down") => void
  /** Multi-line paste → create multiple checklist items */
  onPasteItems?: (id: string, items: ParsedLine[]) => void
  /** Tab → indent item (increase depth) */
  onIndent?: (id: string) => void
  /** Shift+Tab → outdent item (decrease depth) */
  onOutdent?: (id: string) => void
  /** Pre-initialize as editing (prevents bg-accent flicker on component swap) */
  startFocused?: boolean
  /** FIXED recurring template — disable toggle, show read-only circle */
  readOnlyToggle?: boolean
  /**
   * Rendering context:
   * - "detail" (default): inside ChecklistContainer with full keyboard nav
   * - "standalone": self-contained with internal swipe + context menu (chat, search)
   */
  context?: "detail" | "standalone"
  /** Compact rendering — reduced padding (for chat) */
  compact?: boolean
  /** Disable contentEditable — view-only (for chat) */
  readOnly?: boolean
}

// ---------------------------------------------------------------------------
// Debug logging for iOS checklist deletion
// ---------------------------------------------------------------------------
import { clLog } from "@/shared/debug/checklistDebugLog"

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ChecklistItem = memo(
  function ChecklistItem({
    id,
    isDragOverlay,
    isDragActive,
    optimisticItem,
    onSplitAtCursor,
    onMergeWithPrevious,
    onMergeWithNext,
    onDeleteAndFocusPrevious,
    onOpenStatusMenu,
    onNavigatePrevious,
    onNavigateNext,
    isSelected,
    isFirstSelected,
    isLastSelected,
    isDraggedAway,
    onFocusItem,
    onSelectItem,
    onToggleComplete,
    onToggleCancel,
    onDuplicateItem,
    onReorderUp,
    onReorderDown,
    onConvertToSection,
    onShiftArrow,
    onPasteItems,
    onIndent,
    onOutdent,
    startFocused,
    readOnlyToggle,
    context,
    compact,
    readOnly,
  }: ChecklistItemProps) {
    const [dbItem] = useChecklistItemData(id)
    const { t } = useTranslation()
    // Prefer container's optimistic data (has pending overrides applied)
    // over per-item DB query (may be stale during async transactions).
    const item = optimisticItem ?? dbItem
    const titleRef = useRef<HTMLDivElement>(null)
    const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [isEditing, setIsEditing] = useState(startFocused ?? false)

    // Local status override for instant visual feedback.
    // Set synchronously in handleKeyDown, cleared when DB catches up.
    const [localStatus, setLocalStatus] = useState<ObjectStatus | null>(null)
    useEffect(() => {
      if (localStatus !== null && item?.status === localStatus) {
        setLocalStatus(null)
      }
    }, [item?.status, localStatus])

    // --- Standalone mode: internal status menu state -----------------------
    const isStandalone = context === "standalone"
    const [statusMenuPos, setStatusMenuPos] = useState<{ x: number; y: number } | null>(null)

    // Track text just flushed to DB — prevents useLayoutEffect from
    // reverting the DOM to stale item.title between blur and DB update.
    const savedTextRef = useRef<string | null>(null)
    useEffect(() => {
      if (
        savedTextRef.current !== null &&
        (item?.title ?? "") === savedTextRef.current
      ) {
        savedTextRef.current = null
      }
    }, [item?.title])

    // DnD Kit sortable
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id, disabled: isDragOverlay })

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.3 : 1,
    }

    // Cleanup auto-save timer on unmount
    useEffect(() => {
      return () => {
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
      }
    }, [])

    // Flush unsaved text when app goes to background (mobile resilience)
    useEffect(() => {
      const handleVisibility = () => {
        if (document.visibilityState === "hidden" && autoSaveTimer.current) {
          clearTimeout(autoSaveTimer.current)
          autoSaveTimer.current = null
          if (!item?.id || !titleRef.current) return
          const text = titleRef.current.textContent ?? ""
          if (text !== (item.title ?? "")) {
            savedTextRef.current = text
            updateChecklistTitle(item.id, text)
          }
        }
      }
      document.addEventListener("visibilitychange", handleVisibility)
      return () =>
        document.removeEventListener("visibilitychange", handleVisibility)
    }, [item?.id, item?.title])

    // Sync DOM content:
    // - Initial mount: populate contentEditable from item data
    // - External update (multi-user): sync when not editing
    // - Skip when a save is in-flight (savedTextRef) to prevent stale flash
    // - Allow fresh-mount populate even if isEditing (startFocused case)
    // Note: no {item.title} in JSX — React never touches
    // the contentEditable DOM, preventing reconciliation fights.
    useLayoutEffect(() => {
      if (!titleRef.current || !item) return
      const domText = titleRef.current.textContent ?? ""
      const dbTitle = item.title ?? ""

      // If title matches DOM already, nothing to do
      if (domText === dbTitle) return

      // A save is still in flight: the DOM holds exactly the text we just
      // wrote and the DB hasn't caught up yet. Overwriting here is what the
      // comment above calls the "stale flash" — the guard was documented but
      // never actually read. Measured: after Enter this blanked the row for
      // ~1.5s before the text reappeared, because moving focus to the new row
      // flips isEditing and re-runs this effect while item.title is still "".
      if (savedTextRef.current !== null && domText === savedTextRef.current) return

      // External change (undo/redo/sync) — always update DOM.
      // Safe because this effect only fires when item.title changes (dep),
      // and during normal typing item.title doesn't change until auto-save.
      titleRef.current.textContent = dbTitle
      savedTextRef.current = null
    }, [item?.title, isEditing]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Save helpers ────────────────────────────────────────────────────

    const flushSave = useCallback(() => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current)
        autoSaveTimer.current = null
      }
      if (!item?.id || !titleRef.current) return
      const text = titleRef.current.textContent ?? ""
      if (text !== (item.title ?? "")) {
        savedTextRef.current = text
        updateChecklistTitle(item.id, text)
      }
    }, [item?.id, item?.title])

    const handleToggle = useCallback(() => {
      if (!item?.id || readOnlyToggle) return
      toggleChecklistItem(item.id)
    }, [item?.id, readOnlyToggle])

    const handleBlur = useCallback(() => {
      setIsEditing(false)
      flushSave()
    }, [flushSave])

    const handleInput = useCallback((e: FormEvent<HTMLDivElement>) => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)

      // A row is one line: drop a break the browser inserted on Enter,
      // whenever it arrives (see stripLineBreaks).
      if (titleRef.current && !(e.nativeEvent as InputEvent).isComposing) {
        stripLineBreaks(titleRef.current)
      }

      // Mark DOM as dirty — prevents useLayoutEffect from reverting
      // browser-native edits (e.g. deleting selected text) before auto-save.
      if (titleRef.current) {
        savedTextRef.current = titleRef.current.textContent ?? ""
      }

      // ── # auto-conversion: typing "# " at position 0 → convert to section ──
      if (titleRef.current && item?.id) {
        const text = titleRef.current.textContent ?? ""
        if (text.startsWith("# ") || text.startsWith("#\u00a0")) {
          // Strip the "# " prefix (handles both regular space and nbsp),
          // flush remaining text, then convert.
          const stripped = text.slice(2)
          titleRef.current.textContent = stripped
          savedTextRef.current = stripped
          updateChecklistTitle(item.id, stripped)
          onConvertToSection?.(item.id, stripped)
          return
        }
      }

      autoSaveTimer.current = setTimeout(() => {
        if (!item?.id || !titleRef.current) return
        const text = titleRef.current.textContent ?? ""
        if (text !== (item.title ?? "")) {
          updateChecklistTitle(item.id, text)
        }
      }, AUTO_SAVE_DELAY)
    }, [item?.id, item?.title, onConvertToSection])

    // Paste: single line → insert as text;
    // multi-line → first line modifies current item, rest create items below.
    const handlePaste = useCallback(
      (e: React.ClipboardEvent) => {
        e.preventDefault()
        const text = e.clipboardData.getData("text/plain")
        if (!text || !item?.id) return

        // Check for multi-line content
        const rawLines = text.split(/\r?\n/).filter((l) => l.trim().length > 0)
        if (rawLines.length <= 1) {
          // Single line: insert as plain text at cursor
          document.execCommand("insertText", false, text.replace(/\n/g, ""))
          return
        }

        // ── Multi-line paste ──────────────────────────────────────────
        // First line → modify current item (the Anchor).
        // Remaining lines → create new checklist items below.
        const firstRawLine = rawLines[0]!
        const currentText = titleRef.current?.textContent ?? ""

        if (currentText.trim() === "") {
          // Empty field: parse first line's type and apply to current item
          const firstParsed = parseClipboardLines(firstRawLine)[0]
          if (firstParsed && titleRef.current) {
            titleRef.current.textContent = firstParsed.title
            savedTextRef.current = firstParsed.title
            updateChecklistTitle(item.id, firstParsed.title)
            if (firstParsed.type === "section") {
              onConvertToSection?.(item.id, firstParsed.title)
            } else if (firstParsed.status === "completed") {
              onToggleComplete?.(item.id)
            } else if (firstParsed.status === "canceled") {
              onToggleCancel?.(item.id)
            }
          }
        } else {
          // Non-empty field: append raw first line at cursor
          document.execCommand("insertText", false, firstRawLine)
        }

        // Remaining lines → parse and create below
        const remainingText = rawLines.slice(1).join("\n")
        const parsedLines = parseClipboardLines(remainingText)
        if (parsedLines.length > 0) {
          onPasteItems?.(item.id, parsedLines)
        }
      },
      [
        item?.id,
        onPasteItems,
        onConvertToSection,
        onToggleComplete,
        onToggleCancel,
      ],
    )

    // ── Keyboard ────────────────────────────────────────────────────────

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        // Ignore key events during IME composition (iOS virtual keyboard)
        if (e.nativeEvent.isComposing) {
          clLog("keyDown", `SKIP isComposing key=${e.key} id=${item?.id}`)
          return
        }

        // Log all key events for debugging
        if (e.key === "Backspace" || e.key === "Delete" || e.key === "Unidentified") {
          const text = titleRef.current?.textContent ?? "<null>"
          clLog("keyDown", `key=${e.key} text=${JSON.stringify(text)} ` +
            `textLen=${text.length} meta=${e.metaKey} ctrl=${e.ctrlKey} ` +
            `id=${item?.id?.slice(-8)}`)
        }

        // ── Space after "#": convert to section ──
        // Detect in keyDown (before the space is inserted into the DOM)
        // to avoid contentEditable's \u00a0 (nbsp) issues.
        if (
          e.key === " " &&
          !e.metaKey &&
          !e.ctrlKey &&
          !e.shiftKey &&
          !e.altKey
        ) {
          if (titleRef.current && item?.id) {
            const text = titleRef.current.textContent ?? ""
            const offset = getCursorOffset(titleRef.current)
            // Everything before the cursor is exactly "#"
            if (text.slice(0, offset) === "#") {
              e.preventDefault()
              const remaining = text.slice(offset).trim()
              if (autoSaveTimer.current) {
                clearTimeout(autoSaveTimer.current)
                autoSaveTimer.current = null
              }
              titleRef.current.textContent = remaining
              savedTextRef.current = remaining
              updateChecklistTitle(item.id, remaining)
              onConvertToSection?.(item.id, remaining)
              return
            }
          }
        }

        // ── Tab / Shift+Tab: indent / outdent ──
        if (e.key === "Tab") {
          e.preventDefault()
          if (!item?.id) return
          console.log("[INDENT-DEBUG] Tab handler in ChecklistItem", { shiftKey: e.shiftKey, itemId: item.id, hasOnIndent: !!onIndent, hasOnOutdent: !!onOutdent })
          if (e.shiftKey) {
            onOutdent?.(item.id)
          } else {
            onIndent?.(item.id)
          }
          return
        }

        // ── Enter: split — new item ABOVE, stay in current ──
        // MOB-02: deliberately NOT calling e.preventDefault() on the normal
        // path. ProseMirror's input.ts never preventDefaults Enter on iOS
        // either ("if we preventDefault enter key presses, the virtual
        // keyboard gets confused") — the OS capitalization/autocorrect state
        // machine only advances when it observes the real Enter keypress
        // reach the DOM. Blocking it is what caused checklist rows to open
        // mid-sentence. (IME composition is already handled by the
        // `isComposing` guard at the top of this handler.)
        //
        // So: read cursor state synchronously now (before the browser's
        // default action mutates the DOM), then defer the actual split +
        // DOM repair to the next frame, after that mutation has happened.
        if (e.key === "Enter") {
          if (!titleRef.current || !item?.id || item.index === undefined) {
            // No handler will run to repair a stray newline — block it.
            e.preventDefault()
            return
          }

          // Flush any pending debounced save before splitting — Mode A
          // (below) does not itself persist the current item's title, so
          // relying on the 3s debounce firing later risks losing text
          // typed just before Enter.
          flushSave()

          const el = titleRef.current
          const itemId = item.id
          const itemIndex = item.index
          const itemStatus = item.status
          const fullText = el.textContent ?? ""
          const offset = getCursorOffset(el)
          const before = fullText.slice(0, offset)
          const after = fullText.slice(offset)

          requestAnimationFrame(() => {
            if (!el.isConnected) return

            // The browser's own newline insertion may have queued an
            // autosave via onInput — the split logic below is the source
            // of truth for what gets persisted, so drop it.
            if (autoSaveTimer.current) {
              clearTimeout(autoSaveTimer.current)
              autoSaveTimer.current = null
            }

            if (after === "") {
              // Mode A: Cursor at END — current item stays unchanged.
              // Container creates new empty item BELOW and moves cursor there.
              // Repair the browser's inserted newline/<br>, if any — no-op
              // when the browser didn't touch the DOM.
              if ((el.textContent ?? "") !== before || hasNonTextChildren(el)) {
                el.textContent = before
              }
              // Guard against useLayoutEffect reverting to the stale
              // item.title while flushSave's write is still in flight.
              // Mode A needed no guard before, because it never touched the
              // DOM; now that it repairs the browser's inserted newline it
              // does. Measured without this: the row rendered EMPTY for
              // ~1.5s after Enter before the text reappeared.
              savedTextRef.current = before
              onSplitAtCursor?.(itemId, itemIndex, before, after)
            } else {
              // Mode B: Cursor in MIDDLE — current item keeps textAfter.
              if ((el.textContent ?? "") !== after || hasNonTextChildren(el)) {
                el.textContent = after
              }
              setCursorOffset(el, 0)

              // Guard against useLayoutEffect restoring stale item.title
              savedTextRef.current = after

              // Instant visual reset — icon changes THIS frame
              if (itemStatus !== ObjectStatus.Unfinished) {
                setLocalStatus(ObjectStatus.Unfinished)
              }

              onSplitAtCursor?.(itemId, itemIndex, before, after)

              // The card grew (new item above) — scroll anchor into view
              scrollChecklistRowIntoView(id)
            }
          })
          return
        }

        // ── ⌘K: toggle complete ──
        if (
          e.key === "k" &&
          (e.metaKey || e.ctrlKey) &&
          !e.shiftKey &&
          !e.altKey
        ) {
          e.preventDefault()
          e.stopPropagation()
          if (item?.id) {
            // Flush pending text changes before status toggle
            if (autoSaveTimer.current) {
              clearTimeout(autoSaveTimer.current)
              autoSaveTimer.current = null
            }
            flushSave()
            onToggleComplete?.(item.id)
          }
          return
        }

        // ── ⌥⌘K: toggle cancel ──
        if (
          e.code === "KeyK" &&
          (e.metaKey || e.ctrlKey) &&
          e.altKey &&
          !e.shiftKey
        ) {
          e.preventDefault()
          e.stopPropagation()
          if (item?.id) {
            if (autoSaveTimer.current) {
              clearTimeout(autoSaveTimer.current)
              autoSaveTimer.current = null
            }
            flushSave()
            onToggleCancel?.(item.id)
          }
          return
        }

        // ── ⌘⇧H: convert to section ──
        if (
          e.code === "KeyH" &&
          (e.metaKey || e.ctrlKey) &&
          e.shiftKey &&
          !e.altKey
        ) {
          e.preventDefault()
          e.stopPropagation()
          if (item?.id) {
            if (autoSaveTimer.current) {
              clearTimeout(autoSaveTimer.current)
              autoSaveTimer.current = null
            }
            flushSave()
            onConvertToSection?.(item.id)
          }
          return
        }

        // ── ⌘D: duplicate item below ──
        if (
          e.key === "d" &&
          (e.metaKey || e.ctrlKey) &&
          !e.shiftKey &&
          !e.altKey
        ) {
          e.preventDefault()
          e.stopPropagation()
          if (item?.id) {
            if (autoSaveTimer.current) {
              clearTimeout(autoSaveTimer.current)
              autoSaveTimer.current = null
            }
            flushSave()
            onDuplicateItem?.(item.id)
          }
          return
        }

        // ── ⌘↑: reorder up ──
        if (
          e.key === "ArrowUp" &&
          (e.metaKey || e.ctrlKey) &&
          !e.shiftKey &&
          !e.altKey
        ) {
          e.preventDefault()
          e.stopPropagation()
          if (item?.id) onReorderUp?.(item.id)
          return
        }

        // ── ⌘↓: reorder down ──
        if (
          e.key === "ArrowDown" &&
          (e.metaKey || e.ctrlKey) &&
          !e.shiftKey &&
          !e.altKey
        ) {
          e.preventDefault()
          e.stopPropagation()
          if (item?.id) onReorderDown?.(item.id)
          return
        }

        // ── ⌘Backspace: delete item (even if non-empty) ──
        if (e.key === "Backspace" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault()
          e.stopPropagation()
          if (item?.id) {
            if (autoSaveTimer.current) {
              clearTimeout(autoSaveTimer.current)
              autoSaveTimer.current = null
            }
            onDeleteAndFocusPrevious?.(item.id)
          }
          return
        }

        // ── Escape: revert + blur ──
        if (e.key === "Escape") {
          e.preventDefault()
          if (autoSaveTimer.current) {
            clearTimeout(autoSaveTimer.current)
            autoSaveTimer.current = null
          }
          if (titleRef.current && item) {
            titleRef.current.textContent = item.title ?? ""
          }
          titleRef.current?.blur()
          return
        }

        // ── Arrow navigation across items ──
        if (
          e.key === "ArrowLeft" ||
          e.key === "ArrowRight" ||
          e.key === "ArrowUp" ||
          e.key === "ArrowDown"
        ) {
          if (!titleRef.current || !item?.id) return

          // If there's a non-collapsed selection (without shift), let browser
          // collapse it naturally — don't jump to adjacent items.
          if (!e.shiftKey && !e.metaKey && !e.ctrlKey) {
            const sel = window.getSelection()
            if (sel && !sel.isCollapsed) return
          }

          // Shift+Arrow at edge → item-level selection
          if (e.shiftKey) {
            if (e.key === "ArrowUp" && isCursorOnFirstLine(titleRef.current)) {
              e.preventDefault()
              onShiftArrow?.(item.id, "up")
              return
            }
            if (e.key === "ArrowDown" && isCursorOnLastLine(titleRef.current)) {
              e.preventDefault()
              onShiftArrow?.(item.id, "down")
              return
            }
            return // Let browser handle text selection within element
          }

          const fullText = titleRef.current.textContent ?? ""
          const offset = getCursorOffset(titleRef.current)
          const cursorRect = getCursorRect()
          const cursorX = cursorRect?.left ?? 0

          // ArrowLeft at position 0 → end of previous item
          if (e.key === "ArrowLeft" && offset === 0) {
            e.preventDefault()
            // Pass a high X so focusAtXPosition targets end of last line
            onNavigatePrevious?.(item.id, Infinity)
            return
          }

          // ArrowRight at end → start of next item
          if (e.key === "ArrowRight" && offset === fullText.length) {
            e.preventDefault()
            onNavigateNext?.(item.id, 0)
            return
          }

          // ArrowUp on first visual line → previous item, preserve X
          if (e.key === "ArrowUp" && isCursorOnFirstLine(titleRef.current)) {
            e.preventDefault()
            onNavigatePrevious?.(item.id, cursorX)
            return
          }

          // ArrowDown on last visual line → next item, preserve X
          if (e.key === "ArrowDown" && isCursorOnLastLine(titleRef.current)) {
            e.preventDefault()
            onNavigateNext?.(item.id, cursorX)
            return
          }

          // Otherwise: let browser handle normal within-element navigation
          return
        }

        // ── Backspace: merge or delete ──
        if (e.key === "Backspace" || e.key === "Delete") {
          if (!titleRef.current || !item?.id) {
            clLog("keyDown-bs", `BAIL: titleRef=${!!titleRef.current} id=${item?.id}`)
            return
          }

          // If there's a non-collapsed selection, let browser delete it normally
          const sel = window.getSelection()
          if (sel && !sel.isCollapsed) {
            clLog("keyDown-bs", "SKIP: non-collapsed selection")
            return
          }

          const rawText = titleRef.current.textContent ?? ""
          const fullText = rawText.replace(/[\u200B\uFEFF]/g, "").trim()
          const offset = getCursorOffset(titleRef.current)
          clLog("keyDown-bs", `rawText=${JSON.stringify(rawText)} clean=${JSON.stringify(fullText)} offset=${offset} id=${item.id.slice(-8)}`)

          if (e.key === "Backspace") {
            // At position 0 with text → absorb previous item's text
            if (offset === 0 && fullText.length > 0) {
              e.preventDefault()
              if (autoSaveTimer.current) {
                clearTimeout(autoSaveTimer.current)
                autoSaveTimer.current = null
              }
              // Container returns previous item's title synchronously, or undefined
              const prevTitle = onMergeWithPrevious?.(item.id, fullText)
              if (prevTitle !== undefined) {
                // Prepend prev text into our DOM, set cursor at the join point
                const merged = prevTitle + fullText
                titleRef.current.textContent = merged
                setCursorOffset(titleRef.current, prevTitle.length)
                // Guard against stale DB value overwriting the merged DOM
                savedTextRef.current = merged
              }
              return
            }
          }

          // Delete (forward) at end with text → absorb next item's text
          if (e.key === "Delete") {
            if (offset >= fullText.length && fullText.length > 0) {
              e.preventDefault()
              if (autoSaveTimer.current) {
                clearTimeout(autoSaveTimer.current)
                autoSaveTimer.current = null
              }
              const nextTitle = onMergeWithNext?.(item.id, fullText)
              if (nextTitle !== undefined) {
                const merged = fullText + nextTitle
                titleRef.current.textContent = merged
                setCursorOffset(titleRef.current, fullText.length)
                savedTextRef.current = merged
              }
              return
            }
          }

          // Empty → delete row (both Backspace and Delete)
          if (fullText === "") {
            e.preventDefault()
            clLog("keyDown-bs", `DELETE empty row id=${item.id.slice(-8)}`)
            if (autoSaveTimer.current) {
              clearTimeout(autoSaveTimer.current)
              autoSaveTimer.current = null
            }
            onDeleteAndFocusPrevious?.(item.id)
            return
          }
          clLog("keyDown-bs", `NO ACTION: not empty and offset>0`)
        }
      },
      [
        item,
        flushSave,
        onSplitAtCursor,
        onMergeWithPrevious,
        onMergeWithNext,
        onDeleteAndFocusPrevious,
        onNavigatePrevious,
        onNavigateNext,
        onToggleComplete,
        onToggleCancel,
        onDuplicateItem,
        onReorderUp,
        onReorderDown,
        onConvertToSection,
        onShiftArrow,
        onIndent,
        onOutdent,
      ],
    )

    // iOS Safari sends beforeinput with inputType "deleteContentBackward"
    // instead of keyDown with key "Backspace" when the field is empty.
    // Native listener (not React synthetic) for reliable iOS Safari support.
    // Also handles compositionend fallback for when composition masks the
    // beforeinput event (intermittent on iOS virtual keyboard).
    useEffect(() => {
      const el = titleRef.current
      if (!el) return

      // Helper: strip zero-width spaces that WebKit injects in empty contentEditable
      const getCleanText = () =>
        (el.textContent ?? "").replace(/[\u200B\uFEFF]/g, "").trim()

      const handleBeforeInput = (e: InputEvent) => {
        clLog("beforeInput", `inputType=${e.inputType} isComposing=${e.isComposing} id=${item?.id?.slice(-8)}`)
        if (e.inputType !== "deleteContentBackward") return
        if (!titleRef.current || !item?.id) {
          clLog("beforeInput", `BAIL: titleRef=${!!titleRef.current} id=${item?.id}`)
          return
        }

        // If there's a non-collapsed selection, let browser delete it normally
        const sel = window.getSelection()
        if (sel && !sel.isCollapsed) {
          clLog("beforeInput", "SKIP: non-collapsed selection, letting browser handle")
          return
        }

        const rawText = el.textContent ?? ""
        const fullText = getCleanText()
        const offset = getCursorOffset(titleRef.current)
        clLog("beforeInput", `rawText=${JSON.stringify(rawText)} clean=${JSON.stringify(fullText)} offset=${offset}`)

        // Empty → delete row
        if (fullText === "") {
          e.preventDefault()
          clLog("beforeInput", `DELETE empty row id=${item.id.slice(-8)}`)
          if (autoSaveTimer.current) {
            clearTimeout(autoSaveTimer.current)
            autoSaveTimer.current = null
          }
          onDeleteAndFocusPrevious?.(item.id)
          return
        }

        // At position 0 with text → merge with previous
        if (offset === 0) {
          e.preventDefault()
          clLog("beforeInput", `MERGE at pos 0, id=${item.id.slice(-8)}`)
          if (autoSaveTimer.current) {
            clearTimeout(autoSaveTimer.current)
            autoSaveTimer.current = null
          }
          const prevTitle = onMergeWithPrevious?.(item.id, fullText)
          if (prevTitle !== undefined) {
            const merged = prevTitle + fullText
            titleRef.current.textContent = merged
            setCursorOffset(titleRef.current, prevTitle.length)
            savedTextRef.current = merged
          }
        } else {
          clLog("beforeInput", `NO ACTION: offset=${offset} > 0, letting browser handle`)
        }
      }

      // After composition ends (iOS IME), check if the field is now empty.
      // This catches the case where beforeinput was suppressed during composition.
      const handleCompositionEnd = () => {
        clLog("compositionEnd", `text=${JSON.stringify(el.textContent)} id=${item?.id?.slice(-8)}`)
        if (!titleRef.current || !item?.id) return
        // Defer to let the DOM settle after composition commit
        requestAnimationFrame(() => {
          if (!titleRef.current) return
          const text = getCleanText()
          if (text === "" && document.activeElement === titleRef.current) {
            // Field is empty after composition — next backspace should delete.
            // We can't auto-delete here (user might want to type more),
            // but ensure the DOM is truly empty so next beforeinput matches.
            titleRef.current.textContent = ""
          }
        })
      }

      el.addEventListener("beforeinput", handleBeforeInput)
      el.addEventListener("compositionend", handleCompositionEnd)
      return () => {
        el.removeEventListener("beforeinput", handleBeforeInput)
        el.removeEventListener("compositionend", handleCompositionEnd)
      }
    }, [item?.id, onDeleteAndFocusPrevious, onMergeWithPrevious])

    const handleContextMenu = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault()
        if (item?.id) {
          if (isStandalone) {
            // Standalone: open internal status menu
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
            setStatusMenuPos({ x: rect.left, y: rect.bottom + 4 })
          } else {
            onOpenStatusMenu?.(item.id, e.currentTarget as HTMLElement)
          }
        }
      },
      [item?.id, onOpenStatusMenu, isStandalone],
    )

    // --- Standalone swipe action handler ---
    const handleStandaloneSwipe = useCallback(
      (action: ChecklistSwipeAction) => {
        if (!item?.id) return
        if (action === "complete") {
          toggleChecklistItem(item.id)
        } else if (action === "menu") {
          // Open status menu at middle of the row
          const el = document.querySelector(`[data-checklist-id="${id}"]`)
          if (el) {
            const rect = el.getBoundingClientRect()
            setStatusMenuPos({ x: rect.left + rect.width / 2, y: rect.top + rect.height })
          }
        } else if (action === "delete") {
          onDeleteAndFocusPrevious?.(item.id)
        }
      },
      [item?.id, id, onDeleteAndFocusPrevious],
    )

    // Modifier-click on the row → multi-select instead of focus
    const handleRowMouseDown = useCallback(
      (e: React.MouseEvent) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey) {
          e.preventDefault() // Prevent contentEditable from gaining focus
          onSelectItem?.(id, {
            meta: e.metaKey || e.ctrlKey,
            shift: e.shiftKey,
          })
        }
      },
      [id, onSelectItem],
    )

    if (!item) return null

    const displayStatus = localStatus ?? item.status
    const isCompleted = displayStatus === ObjectStatus.Completed
    const isCanceled = displayStatus === ObjectStatus.Canceled

    const rowContent = (
      <div
        ref={setNodeRef}
        style={{ ...style, paddingLeft: (item.depth ?? 0) * 24 + (compact ? 6 : 10) }}
        {...attributes}
        data-checklist-id={id}
        onMouseDown={handleRowMouseDown}
        className={cn(
          "group pr-2.5",
          compact ? "py-1" : "py-1.5",
          "transition-colors duration-150",
          // Rounding: full when solo, flat edges between consecutive selected items
          isSelected && !isFirstSelected && !isLastSelected && !isDragOverlay
            ? "rounded-none"
            : isSelected && !isFirstSelected && !isDragOverlay
              ? "rounded-b-md rounded-t-none"
              : isSelected && !isLastSelected && !isDragOverlay
                ? "rounded-t-md rounded-b-none"
                : "rounded-md",
          isEditing && !isSelected && !isDragOverlay && "bg-foreground/3",
          isSelected && !isDragOverlay && "bg-foreground/3",
          isDraggedAway && "opacity-30 transition-opacity duration-200",
          isDragOverlay && "bg-card shadow-md",
        )}
      >
        {/* Row: checkbox + title + actions */}
        <div className="flex items-start gap-2">
        {/* Checkbox icon */}
        <button
          type="button"
          className={cn(
            "mt-0.75 shrink-0 bg-transparent border-none p-0",
            readOnlyToggle ? "cursor-default opacity-50" : "cursor-pointer",
          )}
          onClick={handleToggle}
          onContextMenu={handleContextMenu}
          aria-label={
            displayStatus === ObjectStatus.Completed
              ? t("Completed")
              : displayStatus === ObjectStatus.Canceled
                ? t("Canceled")
                : t("{{count}}% complete", { count: item.progress ?? 0 })
          }
        >
          <DynamicIcon
            type="project"
            percentage={item.progress ?? 0}
            unfinished={0}
            status={displayStatus}
            size="small"
            className={cn(isCanceled && "opacity-40")}
            color={
              displayStatus === ObjectStatus.Unfinished &&
              normalizeUserStatus(item.userStatus)
                ? getUserStatusDisplay(item.userStatus).color
                : displayStatus === ObjectStatus.Unfinished
                  ? "var(--checkbox-border)"
                  : isCanceled
                    ? "var(--foreground)"
                    : "var(--primary)"
            }
          />
        </button>

        {/* Editable title — NO visible focus ring */}
        <div
          ref={titleRef}
          contentEditable={!isDragOverlay && !readOnly && !isDragActive}
          suppressContentEditableWarning
          role="textbox"
          aria-label={t("Checklist item")}
          autoCapitalize="sentences"
          autoCorrect="on"
          spellCheck={true}
          enterKeyHint="enter"
          className={cn(
            "min-w-0 flex-1 text-base outline-none leading-snug py-0.5",
            isCompleted && "text-muted-foreground",
            isCanceled && "text-muted-foreground line-through opacity-60",
          )}
          onMouseDown={(e) => {
            // Intercept modifier-clicks BEFORE the contentEditable gains focus
            if (e.metaKey || e.ctrlKey || e.shiftKey) {
              e.preventDefault()
              e.stopPropagation() // Don't let the row handler also fire
              onSelectItem?.(id, {
                meta: e.metaKey || e.ctrlKey,
                shift: e.shiftKey,
              })
            }
          }}
          onFocus={() => {
            setIsEditing(true)
            onFocusItem?.(id)
          }}
          onBlur={handleBlur}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
        />

        {/* Add image button — visible on hover or when editing */}
        <ChecklistImageButton
          itemId={id}
          disabled={isDragOverlay}
          isEditing={isEditing}
        />

        {/* Drag handle — right side, visible on hover (hidden in standalone) */}
        {!isStandalone && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...listeners}
          className={cn(
            "mt-1.5 shrink-0 cursor-grab touch-none",
            // Permanently visible on touch (see image-button comment above).
            "opacity-0 any-pointer-coarse:opacity-40 group-hover:opacity-40 transition-opacity duration-150",
            isDragOverlay && "opacity-40",
          )}
          aria-label={t("Drag to reorder")}
          tabIndex={-1}
        >
          <GripVertical
            width={14}
            height={14}
            style={{ color: "var(--muted-foreground)" }}
          />
        </button>
        )}
        </div>

        {/* Attached images — thumbnails below the row */}
        <ChecklistImages itemId={id} />
      </div>
    )

    // --- Standalone mode: wrap in SwipeableChecklistRow + render status menu ---
    if (isStandalone) {
      return (
        <>
          <SwipeableChecklistRow
            onAction={handleStandaloneSwipe}
            depth={item.depth ?? 0}
          >
            {rowContent}
          </SwipeableChecklistRow>
          {statusMenuPos && (
            <ChecklistStatusMenu
              itemId={id}
              currentStatus={item.status ?? ObjectStatus.Unfinished}
              currentProgress={item.progress}
              currentUserStatus={item.userStatus}
              currentSubType={item.subType}
              position={statusMenuPos}
              onClose={() => setStatusMenuPos(null)}
            />
          )}
        </>
      )
    }

    return rowContent
  },
  // Custom memo: ID + overlay + selection + key data fields from optimisticItem.
  // Data-field comparison ensures container-level liveQuery updates (e.g.
  // userStatus change) propagate through memo even though per-item
  // liveQuery + `optimisticItem ?? dbItem` precedence would otherwise
  // show stale data.  Value comparison keeps this cheap — only the
  // actually-changed item will get a new value; unchanged items still
  // short-circuit.
  (prev, next) =>
    prev.id === next.id &&
    prev.isDragOverlay === next.isDragOverlay &&
    // Without this the rows never re-render when a drag starts, so the
    // contentEditable-off workaround for the iOS repaint bug never applies.
    prev.isDragActive === next.isDragActive &&
    prev.isSelected === next.isSelected &&
    prev.isFirstSelected === next.isFirstSelected &&
    prev.isLastSelected === next.isLastSelected &&
    prev.isDraggedAway === next.isDraggedAway &&
    prev.context === next.context &&
    prev.compact === next.compact &&
    prev.readOnly === next.readOnly &&
    prev.optimisticItem?.userStatus === next.optimisticItem?.userStatus &&
    prev.optimisticItem?.status === next.optimisticItem?.status &&
    prev.optimisticItem?.title === next.optimisticItem?.title &&
    prev.optimisticItem?.subType === next.optimisticItem?.subType &&
    prev.optimisticItem?.progress === next.optimisticItem?.progress &&
    prev.optimisticItem?.depth === next.optimisticItem?.depth,
)
